<script setup lang="ts">
/**
 * On-brand shimmer placeholder for WORDS (labels, headings, names) and any
 * generic block awaiting data. Replaces the flat gray `animate-pulse` bars
 * with the firm-green sweeping shimmer (see the loading & motion block in
 * main.css), which honors the user's reduce-motion preference automatically.
 *
 *   <AppSkeleton width="w-32" />                    <!-- a word -->
 *   <AppSkeleton width="w-40" height="h-6" strong /> <!-- a heading -->
 *   <AppSkeleton circle height="h-9" width="w-9" />  <!-- an avatar -->
 *
 * For multi-line text blocks pass `lines` (the last line is shortened).
 *
 * `strong` darkens the placeholder a step. Use it for the bar standing in
 * for a heading or a header cell, so the skeleton carries the same visual
 * hierarchy the real content will — a wall of identical bars reads as
 * "broken", a hierarchy reads as "loading".
 *
 * `wave` staggers the sheen. Give sibling skeletons increasing values and
 * the highlight walks across them instead of every bar flashing in unison,
 * which at any density looks like a strobe.
 */
withDefaults(defineProps<{
  width?: string
  height?: string
  rounded?: string
  circle?: boolean
  lines?: number
  strong?: boolean
  wave?: number
}>(), {
  width: 'w-24',
  height: 'h-4',
  rounded: 'rounded',
  circle: false,
  lines: 1,
  strong: false,
  wave: 0
})
</script>

<template>
  <span
    v-if="lines <= 1"
    class="azzad-shimmer inline-block align-middle"
    :class="[circle ? 'rounded-full' : rounded, width, height, { 'azzad-shimmer-strong': strong }]"
    :style="{ '--azzad-wave': wave }"
    aria-hidden="true"
  />
  <span v-else class="flex flex-col gap-2" aria-hidden="true">
    <span
      v-for="i in lines"
      :key="i"
      class="azzad-shimmer block"
      :class="[rounded, height, i === lines ? 'w-2/3' : width, { 'azzad-shimmer-strong': strong && i === 1 }]"
      :style="{ '--azzad-wave': wave + i - 1 }"
    />
  </span>
</template>
